// src/components/player/tiles/Tile4TrendVelocity.js
// Tile 4: Trend Velocity Analysis
// Shows player's performance trends across rolling 5-game windows

import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { Zap } from 'lucide-react';

const Tile4TrendVelocity = ({ data, loading, error }) => {
  // Tile wrapper component
  const Tile = ({ children }) => (
    <div className={`${dynastyTheme.components.card.base} h-full`}>
      {children}
    </div>
  );

  // Format stat name for display
  const formatStatName = (fieldName) => {
    const labels = {
      'batting_avg': 'AVG',
      'obp': 'OBP',
      'slg': 'SLG',
      'ops': 'OPS',
      'home_runs': 'HR',
      'rbi': 'RBI',
      'stolen_bases': 'SB',
      'runs': 'R'
    };
    return labels[fieldName] || fieldName.toUpperCase();
  };

  // Get trend color class
  const getTrendColor = (direction) => {
    if (direction === 'improving') return dynastyTheme.classes.text.success;
    if (direction === 'declining') return dynastyTheme.classes.text.error;
    return dynastyTheme.classes.text.neutral;
  };

  // Get overall trend color
  const getOverallTrendColor = (overallTrend) => {
    if (overallTrend?.includes('🔥')) return dynastyTheme.classes.text.success;
    if (overallTrend?.includes('📈')) return dynastyTheme.classes.text.success;
    if (overallTrend?.includes('❄️')) return dynastyTheme.classes.text.error;
    return dynastyTheme.classes.text.neutral;
  };

  // Check data structure
  const hasTrends = data?.trends;
  const hasOverallTrend = data?.overall_trend;
  const hasMomentum = data?.momentum;

  return (
    <Tile>
      <div className="pt-1 px-4 pb-3 flex flex-col tile-scroll" style={{ height: '182px', overflow: 'auto' }}>
        <h4 className={`text-base font-semibold ${dynastyTheme.classes.text.primary} mb-3 flex items-center`}>
          <Zap className="w-4 h-4 mr-2" />
          📈 Trend Analysis
        </h4>
        
        {loading ? (
          <div className={`flex-1 flex items-center justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Loading analytics...
            </div>
          </div>
        ) : error ? (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.error} mb-2`}>
              {error}
            </div>
          </div>
        ) : (hasTrends && Object.keys(data.trends).length > 0) && hasOverallTrend ? (
          <div className="flex-1 flex flex-col">
            {/* Overall Trend */}
            <div className="text-center mb-3 pb-3 border-b border-gray-700">
              <div className={`text-base ${getOverallTrendColor(data.overall_trend)}`}>
                {data.overall_trend}
              </div>
            </div>

            {/* Individual Stat Trends */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {Object.keys(data.trends).map((stat) => {
                const trend = data.trends[stat];
                const trendColor = getTrendColor(trend.direction);

                return (
                  <div key={stat} className="flex justify-between items-center">
                    <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                      {formatStatName(stat)}
                    </span>
                    <span className={`text-sm ${trendColor}`}>
                      {trend.symbol} {trend.change_pct >= 0 ? '+' : ''}{trend.change_pct.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>
            
            {/* Footer with momentum summary */}
            {hasMomentum && (
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} pt-2 mt-2 border-t border-gray-700`}>
                <div>{data.momentum}</div>
                {data.windows && (
                  <div className="text-sm opacity-75 mt-1">
                    {data.windows.length} rolling windows analyzed
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} text-center`}>
              Need 10+ games for trend analysis
            </div>
            <div className={`text-xs ${dynastyTheme.classes.text.neutral} mt-2 text-center`}>
              (Currently: {data?.windows?.length || 0} window{data?.windows?.length !== 1 ? 's' : ''})
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};

export default Tile4TrendVelocity;
